import { Option } from "@powerledger/ui-component-lib";
import currency from "currency.js";
import { subYears } from "date-fns";
import { FormikHelpers } from "formik";
import { find } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SingleValue } from "react-select";

import { notifications } from "@/app/container/notifications";
import { AppUserAccountInfo, useAppContext } from "@/app/context";
import { useActiveRegistryDisplayName } from "@/app/hooks/use-active-registry-display-name";
import { useAllRecOptions } from "@/app/hooks/use-all-rec-options";
import { useAllFeatureToggleValues } from "@/app/hooks/use-feature-toggle/use-all-feature-toggle-values";
import { useTransactionFee } from "@/app/hooks/use-transaction-fee/use-transaction-fee";
import { getDatePart } from "@/app/lib/date-helpers";
import { getRegistryCodeForTransactionFee, SupportedLinkedRegistry } from "@/app/lib/transaction-fee-helpers";
import {
  AccountType,
  AssetType,
  CurrencyCode,
  RegistryCode,
  TradePosition,
  TradeType,
  useCreateTradeContractMutation,
  useTenantsQuery,
} from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, ANY_REGISTRY_CODE, TraceXSupportedRegistries } from "@/app/types/okta.types";

import { useFilter } from "../../page-header/filter";
import { FilterValue, UseFilterArgs } from "../../page-header/filter/filter.types";
import { OrderLifeCycle } from "../common";
import {
  ForwardTradeFormValues,
  ForwardTradeOrderModalProps,
  UseForwardTradeModal,
} from "./forward-trade-order-modal.types";

const getDefaultAssetsAndRegistryCode = (
  linkedRegistries: AppUserAccountInfo["linkedRegistries"],
  enableCrossRegistryForwardTrading: boolean,
  defaultAsset: ForwardTradeOrderModalProps["defaultAsset"],
  defaultRegistryCode: AggregatedRegistryCode,
) => {
  if (
    !enableCrossRegistryForwardTrading ||
    Number(linkedRegistries?.length) > 1 ||
    defaultRegistryCode === linkedRegistries?.[0]?.registryCode
  ) {
    return { defaultAsset, defaultRegistryCode: defaultRegistryCode ?? ANY_REGISTRY_CODE };
  }

  return {
    defaultRegistryCode: linkedRegistries?.[0]?.registryCode as AggregatedRegistryCode,
  };
};

const disablePlacingSellOrder = (
  linkedRegistries: AppUserAccountInfo["linkedRegistries"],
  registryCode: AggregatedRegistryCode,
) => {
  if (registryCode === ANY_REGISTRY_CODE) {
    return Boolean(linkedRegistries?.some((reg) => reg.accountType === AccountType.NonRegistry));
  }

  return linkedRegistries?.find((reg) => reg.registryCode === registryCode)?.accountType === AccountType.NonRegistry;
};

const isDefaultAssetValuePresent = (defaultAsset: ForwardTradeOrderModalProps["defaultAsset"]) => {
  return Object.values(defaultAsset || {}).some((value) => value !== undefined && value !== null && value.length > 0);
};

export const useForwardTradeModal: UseForwardTradeModal = ({
  closeModal,
  defaultAsset: defaultAssetProps,
  defaultRegistryCode: defaultRegistryCodeProps,
}) => {
  const { userAccountInfo, userSelections } = useAppContext();
  const {
    toggles: { enableCrossRegistryForwardTrading },
  } = useAllFeatureToggleValues();

  const { defaultAsset, defaultRegistryCode } = getDefaultAssetsAndRegistryCode(
    userAccountInfo?.linkedRegistries,
    enableCrossRegistryForwardTrading,
    defaultAssetProps,
    defaultRegistryCodeProps,
  );

  const [selectedRegistryCode, setSelectedRegistryCode] = useState<AggregatedRegistryCode>(
    defaultRegistryCode ?? ANY_REGISTRY_CODE,
  );
  const { t } = useTranslation();

  const registryCode = enableCrossRegistryForwardTrading
    ? selectedRegistryCode
    : (userSelections?.registry?.registryCode as TraceXSupportedRegistries);

  const transactionFeeRegistryCode = useMemo(() => {
    if (!enableCrossRegistryForwardTrading) {
      return userSelections?.registry?.registryCode as TraceXSupportedRegistries;
    }

    if (selectedRegistryCode === ANY_REGISTRY_CODE) {
      return (
        getRegistryCodeForTransactionFee(userAccountInfo?.linkedRegistries as SupportedLinkedRegistry[]) ??
        selectedRegistryCode
      );
    }

    return selectedRegistryCode;
  }, [enableCrossRegistryForwardTrading, selectedRegistryCode, userAccountInfo, userSelections]);

  const isActiveRegistryErcot = registryCode === RegistryCode.Ercot;
  const isActiveRegistryMrets = registryCode === RegistryCode.Mrets;
  const isActiveRegistryAny = registryCode == ANY_REGISTRY_CODE;

  const [tradePosition, setTradePosition] = useState<TradePosition>(TradePosition.Bid);
  const isPositionBid = tradePosition === TradePosition.Bid;
  const [currentStep, setCurrentStep] = useState(OrderLifeCycle.Form);

  /**
   * @todo Enhance the user experience by showing a message why the sell order is disabled for the selected registry
   * @note For the time being, a generic message is displayed on registry label tooltip
   */
  const isSellOrderDisabled = enableCrossRegistryForwardTrading
    ? disablePlacingSellOrder(userAccountInfo?.linkedRegistries, registryCode)
    : userAccountInfo?.accountType === AccountType.NonRegistry;

  const [overLimitError, setOverLimitError] = useState<string>();
  const { data: tenantsData } = useTenantsQuery();
  const [createOrder] = useCreateTradeContractMutation();

  const { getAppliedFee, loading: transactionFeeLoading } = useTransactionFee({
    registryCode: transactionFeeRegistryCode,
  });

  const { activeRegistryDisplayName, registriesQueryLoading } = useActiveRegistryDisplayName(registryCode);

  const [currencyCode, currencySymbol] = useMemo(() => {
    const tenant = find(tenantsData?.tenants, { id: userAccountInfo?.tenantId });
    return [tenant?.localisation?.currencyCode as CurrencyCode, tenant?.localisation?.currencySymbol || "$"];
  }, [userAccountInfo?.tenantId, tenantsData]);

  const { recOptionsByRegistryCode, loading: allRecOptionsLoading } = useAllRecOptions();

  const filterArgs = useMemo(() => {
    const args: UseFilterArgs = {
      hideStatusFilter: true,
      hideProjectFilter: true,
      shouldSelectVintage: true,
      hideLocationFilter: isActiveRegistryErcot, // Hide location filter for ERCOT, but send default value of TX on submission
      hideCertificateTypeFilter: isActiveRegistryMrets, // Hide certificate type filter for MRETS, but send default value of Regular on submission
      filterFormFieldProps: {
        vintages: {
          label: t("Vintage *"),
          ...(enableCrossRegistryForwardTrading && isActiveRegistryAny
            ? {
                info: t(
                  "Select First Half (FH) or Second Half (BH). ERCOT quarters are automatically mapped to FH (Q1/Q2) or BH (Q3/Q4) during assignment.",
                ),
              }
            : {}),
        },
        eligibilities: {
          label: t(!enableCrossRegistryForwardTrading && isActiveRegistryErcot ? "Certification" : "Eligibility"),
          ...(isActiveRegistryErcot
            ? {}
            : {
                info: t(
                  "Eligibilities exist to inform users that Certificates meet the requirements of different external voluntary or compliance programs managed by states",
                ),
              }),
        },
        fuelSources: {
          label: t(!enableCrossRegistryForwardTrading && isActiveRegistryErcot ? "Technology Type" : "Fuel Type"),
        },
        cod: {
          label: t("Commencement of Operations Date (COD)"),
          dynamicInfo: (filterValues: FilterValue[]) =>
            filterValues.length
              ? t(`RECs that are from projects no older than {{date}} will be shown.`, {
                  date: getDatePart(subYears(new Date(), +filterValues?.[0]?.value)),
                })
              : "",
        },
      },
      registryCode,
      ...(defaultAsset
        ? {
            initialValues: {
              ...defaultAsset,
              statuses: [],
              projects: [],
              actionCodes: [],
              dateRange: [],
            },
          }
        : {}),
    };
    return args;
  }, [
    defaultAsset,
    enableCrossRegistryForwardTrading,
    isActiveRegistryAny,
    isActiveRegistryErcot,
    isActiveRegistryMrets,
    registryCode,
    t,
  ]);

  const { filters, filterValues, stackedValues, removeValue, resetFilter } = useFilter(filterArgs);

  const disableRegistryFilter = isDefaultAssetValuePresent(defaultAsset);

  const handleSubmit = useCallback(
    async (values: ForwardTradeFormValues, actions: FormikHelpers<ForwardTradeFormValues>) => {
      const defaultMretsCertificateType =
        recOptionsByRegistryCode[RegistryCode.Mrets]?.certificateTypeOptions?.[0]?.value;
      const defaultErcotLocation = recOptionsByRegistryCode[RegistryCode.Ercot]?.locationOptions?.flatMap(
        (o) => o.options,
      )?.[0]?.value;

      try {
        await createOrder({
          variables: {
            input: {
              tradeContractInput: {
                allowCrossRegistryMatching: enableCrossRegistryForwardTrading && registryCode === ANY_REGISTRY_CODE,
                registryCode,
                assetType: AssetType.Rec,
                tradeType: TradeType.Forward,
                currencyCode,
                position: tradePosition,
                volume: Number(values.volume),
                unitPrice: currency(values.unitPrice).intValue.toString(),
                tradeSettlementDate: values.settlementDate,
              },
              recAttributeInput: {
                vintages: filterValues.vintages.map((selectedOption) => selectedOption.value),
                fuelSources: filterValues.fuelSources.map((selectedOption) => selectedOption.value),
                certifications: filterValues.certifications.map((selectedOption) => selectedOption.value),
                commencementDate: filterValues.cod.length
                  ? getDatePart(subYears(new Date(), +filterValues.cod?.[0]?.value))
                  : undefined,
                certificateTypes:
                  enableCrossRegistryForwardTrading && isActiveRegistryMrets && defaultMretsCertificateType
                    ? [defaultMretsCertificateType]
                    : filterValues.certificateType.map((selectedOption) => selectedOption.value),
                eligibilities: filterValues.eligibilities.map((selectedOption) => selectedOption.value),
                locations:
                  enableCrossRegistryForwardTrading && isActiveRegistryErcot && defaultErcotLocation
                    ? [defaultErcotLocation]
                    : filterValues.locations.map((selectedOption) => selectedOption.value),
              },
            },
          },
          refetchQueries: ["TradeContracts"],
        });
        setCurrentStep(OrderLifeCycle.Success);
      } catch (e) {
        e instanceof Error &&
          notifications.error({
            description: t(e?.message),
          });
      } finally {
        actions.setSubmitting(false);
      }
    },
    [
      recOptionsByRegistryCode,
      createOrder,
      enableCrossRegistryForwardTrading,
      registryCode,
      currencyCode,
      tradePosition,
      filterValues.vintages,
      filterValues.fuelSources,
      filterValues.certifications,
      filterValues.cod,
      filterValues.certificateType,
      filterValues.eligibilities,
      filterValues.locations,
      isActiveRegistryMrets,
      isActiveRegistryErcot,
      t,
    ],
  );

  const toggleTradePosition = useCallback(() => {
    setTradePosition((prev) => (prev === TradePosition.Bid ? TradePosition.Ask : TradePosition.Bid));
  }, []);

  const fee = useMemo(() => getAppliedFee(tradePosition), [getAppliedFee, tradePosition]);

  const formikInitialValue: ForwardTradeFormValues = useMemo(
    () => ({
      volume: 0,
      unitPrice: 0,
      settlementDate: defaultAsset?.selectedDate?.[0]?.value ?? "",
      vintage: defaultAsset?.vintages?.map((vintage) => vintage.value).join(",") ?? "",
      tncAgreed: false,
      disclosureAgreement: false,
    }),
    [defaultAsset?.selectedDate, defaultAsset?.vintages],
  );

  const handleFieldChange = (
    name: "vintage" | "settlementDate" | "registryCode",
    value: any,
    setFieldValue: FormikHelpers<ForwardTradeFormValues>["setFieldValue"],
  ) => {
    if (name === "vintage") {
      if (Array.isArray(value)) {
        const _value = value.map((v) => v.value).join(",");
        setFieldValue(name, _value);
        return;
      }

      const singleValue = value as SingleValue<Option>;
      if (singleValue) {
        setFieldValue(name, singleValue.value);
      } else {
        setFieldValue(name, "");
      }
    } else if (name === "registryCode") {
      setSelectedRegistryCode(value);
      resetFilter();
    } else {
      setFieldValue(name, value);
    }
  };

  /**
   * @todo Enhance the user experience by showing a message why the given registry is disabled for selling orders
   * @note For the time being, a generic message is displayed on registry label tooltip
   */
  const disabledRegistriesForSellOrder = useMemo(() => {
    if (tradePosition !== TradePosition.Ask) return [];

    const nrmRegistries = userAccountInfo?.linkedRegistries
      ?.filter((registry) => registry.accountType === AccountType.NonRegistry)
      .map((registry) => registry.registryCode) as AggregatedRegistryCode[];

    if (nrmRegistries.length > 0) {
      nrmRegistries.push(ANY_REGISTRY_CODE);
    }

    return nrmRegistries;
  }, [userAccountInfo?.linkedRegistries, tradePosition]);

  return {
    loading: registriesQueryLoading || allRecOptionsLoading,
    transactionFeeLoading,
    fee,
    filters,
    stackedValues,
    removeValue,
    isFilterValid: !!filterValues.vintages.length,
    formikInitialValue,
    handleSubmit,
    currencySymbol,
    overLimitError,
    setOverLimitError,
    closeModal,
    currentStep,
    tradePosition,
    isPositionBid,
    toggleTradePosition,
    isSellOrderDisabled,
    handleFieldChange,
    selectedRegistryCode,
    disableRegistryFilter,
    activeRegistryDisplayName,
    disabledRegistriesForSellOrder,
    preventPlacingOrdersInAnyRegistry: Number(userAccountInfo?.linkedRegistries?.length) <= 1,
    enableCrossRegistryForwardTrading,
  };
};
